import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { concat } from '@ember/helper';
import { modifier } from 'ember-modifier';
import style from 'ember-style-modifier/modifiers/style';
import { arcPath } from '../../utils/charts/svg-utils.ts';
import { DEFAULT_CHART_COLORS } from '../../utils/charts/color-palette.ts';
import { formatNumber } from '../../utils/charts/chart-layout.ts';
import { createDebouncedResizeObserver } from '../../utils/charts/resize-observer-utils.ts';
import clampTooltip from '../../modifiers/clamp-tooltip.ts';
import type { ChartDataPoint, ChartOptions } from '../../types/charts.ts';

// ---------------------------------------------------------------------------
// DonutChart – pure SVG donut/ring chart with interactive arcs & legend
// ---------------------------------------------------------------------------

interface DonutChartSignature {
  Args: ChartOptions & {
    /** Chart data points */
    data: ChartDataPoint[];
    /** Optional value formatter (currency/divisor/decimals); defaults to formatNumber. */
    formatValue?: (value: number) => string;
    /** Ring thickness in pixels (default: 60) */
    ringThickness?: number;
    /** Optional max size in pixels — caps the chart when the container is wide (default: no cap) */
    maxSize?: number;
    /** Pin the chart to an exact pixel size, bypassing ResizeObserver entirely */
    size?: number;
    /** Bounded tile height (px). When set, enables scale-to-fit (see fit mode). */
    height?: number;
  };
  Element: HTMLDivElement;
}

interface ArcSegment {
  path: string;
  color: string;
  data: ChartDataPoint;
  startAngle: number;
  endAngle: number;
  percent: number;
  valueLabel: string;
}

const RING_THICKNESS = 60;
const MIN_SIZE = 160;
// Fit mode (opt-in via a bounded @height): draw in a fixed coordinate space and
// scale the SVG into the measured box so the ring shrinks to fit instead of
// clipping. Consumers that don't pass @height keep the legacy width-based sizing.
const FIT_VIEW = 300;
const MIN_FIT = 44;

export default class DonutChart extends Component<DonutChartSignature> {
  @tracked containerWidth = 300;
  @tracked containerHeight = 300;
  @tracked hoveredIndex: number | null = null;
  @tracked tooltipX = 0;
  @tracked tooltipY = 0;

  get colors() {
    return this.args.colors ?? DEFAULT_CHART_COLORS;
  }

  get rawTotal() {
    return this.args.data.reduce((sum, d) => sum + d.value, 0);
  }
  // Clamped to ≥1 only so segment fractions never divide by zero; display uses rawTotal.
  get total() {
    return this.rawTotal || 1;
  }
  get formattedTotal() {
    return (this.args.formatValue ?? formatNumber)(this.rawTotal);
  }

  get isFit() {
    return this.args.height != null;
  }

  get size() {
    // If an explicit size is provided, use it directly — no ResizeObserver needed
    if (this.args.size) return this.args.size;
    const usable = Math.min(this.containerWidth, this.containerHeight);
    const capped = this.args.maxSize ? Math.min(usable, this.args.maxSize) : usable;
    return Math.max(MIN_SIZE, capped);
  }

  /** Rendered SVG px in fit mode (legacy mode uses width:100% + maxWidth:size). */
  get renderPx() {
    return Math.max(MIN_FIT, Math.min(this.containerWidth, this.containerHeight));
  }

  /** Drawing coordinate space — fixed in fit mode so the vector scales. */
  get coordSpace() {
    return this.isFit ? FIT_VIEW : this.size;
  }

  get svgWidth() {
    return this.isFit ? `${this.renderPx}px` : '100%';
  }
  get svgHeight() {
    return this.isFit ? `${this.renderPx}px` : '';
  }
  get svgMaxWidth() {
    return this.isFit ? '' : `${this.size}px`;
  }

  get ringThickness() {
    return this.args.ringThickness ?? RING_THICKNESS;
  }

  get outerRadius() {
    return (this.coordSpace - 4) / 2;
  }

  get innerRadius() {
    return Math.max(0, this.outerRadius - this.ringThickness);
  }

  get cx() {
    return this.coordSpace / 2;
  }

  get cy() {
    return this.coordSpace / 2;
  }

  get viewBox() {
    return `0 0 ${this.coordSpace} ${this.coordSpace}`;
  }

  get segments(): ArcSegment[] {
    const { data } = this.args;
    if (!data.length) return [];

    let currentAngle = 0;

    return data.map((d, i) => {
      const fraction = d.value / this.total;
      const startAngle = currentAngle;
      const endAngle = currentAngle + fraction * Math.PI * 2 - 0.005;
      currentAngle += fraction * Math.PI * 2;

      return {
        path: arcPath({
          cx: this.cx,
          cy: this.cy,
          outerRadius: this.outerRadius,
          innerRadius: this.innerRadius,
          startAngle,
          endAngle: Math.max(endAngle, startAngle + 0.01),
        }),
        color: this.colors[i % this.colors.length]!,
        data: d,
        startAngle,
        endAngle,
        percent: Math.round(fraction * 100),
        valueLabel: (this.args.formatValue ?? formatNumber)(d.value),
      };
    });
  }

  get showLegend() {
    return this.args.showLegend !== false;
  }

  get legendPosition() {
    return this.args.legendPosition ?? 'bottom';
  }

  get isSideLegend() {
    return this.legendPosition === 'left' || this.legendPosition === 'right';
  }

  // Flex direction places the legend on the requested side; default 'bottom'
  // reproduces the original column layout.
  get rootClass() {
    let base: string;
    switch (this.legendPosition) {
      case 'top':
        base = 'flex flex-col-reverse items-center justify-center gap-4';
        break;
      case 'left':
        base = 'flex flex-row-reverse items-center justify-center gap-4';
        break;
      case 'right':
        base = 'flex flex-row items-center justify-center gap-4';
        break;
      default:
        base = 'flex flex-col items-center justify-center gap-4';
    }
    // Fit mode fills the given box so the chart area (flex-1) can shrink to fit.
    return this.isFit ? `${base} h-full min-h-0` : base;
  }

  get chartWrapperClass() {
    const base = 'tio-flex-center relative flex-1 min-h-0';
    return this.isSideLegend ? base : `${base} w-full`;
  }

  get legendClass() {
    return this.isSideLegend
      ? 'flex flex-col gap-2 text-sm shrink-0'
      : 'flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm';
  }

  get legendItems() {
    return this.args.data.map((d, i) => ({
      label: d.category,
      color: this.colors[i % this.colors.length]!,
      value: d.value,
      percent: Math.round((d.value / this.total) * 100),
    }));
  }

  get tooltipData() {
    if (this.hoveredIndex === null) return null;
    const seg = this.segments[this.hoveredIndex];
    if (!seg) return null;
    return {
      category: seg.data.category,
      value: seg.valueLabel,
      percent: seg.percent,
    };
  }

  handleSegmentClick = (segment: ArcSegment) => {
    this.args.onClick?.(segment.data);
  };

  handleSegmentKeydown = (segment: ArcSegment, event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleSegmentClick(segment);
    }
  };

  setHovered = (index: number) => {
    this.hoveredIndex = index;
  };

  clearHovered = () => {
    this.hoveredIndex = null;
  };

  trackPointer = (event: MouseEvent) => {
    this.tooltipX = event.clientX;
    this.tooltipY = event.clientY - 8;
  };

  // Keyboard focus carries no cursor coords — anchor the tooltip to the focused
  // segment's box so it doesn't render at (0,0).
  trackFocus = (event: FocusEvent) => {
    if (!(event.target instanceof Element)) return;
    const rect = event.target.getBoundingClientRect();
    this.tooltipX = rect.left + rect.width / 2;
    this.tooltipY = rect.top;
  };

  installObserver = modifier((element: HTMLElement) => {
    const updateSize = (entry?: ResizeObserverEntry) => {
      let width: number;
      let height: number;

      if (entry) {
        ({ width, height } = entry.contentRect);
      } else {
        const rect = element.getBoundingClientRect();
        width = rect.width || element.clientWidth || 300;
        height = rect.height || element.clientHeight || width;
      }

      this.containerWidth = width || 300;
      this.containerHeight = height || this.containerWidth;
    };

    const observer = createDebouncedResizeObserver((entry) => {
      updateSize(entry);
    });
    observer.observe(element);
    updateSize();

    return () => observer.disconnect();
  });

  <template>
    <div data-test-chart data-test-chart-type="donut" class={{this.rootClass}} ...attributes>
      <div class={{this.chartWrapperClass}} {{this.installObserver}}>
        <svg
          role="img"
          aria-label="Donut chart"
          {{style width=this.svgWidth height=this.svgHeight maxWidth=this.svgMaxWidth}}
          viewBox={{this.viewBox}}
          preserveAspectRatio="xMidYMid meet"
          class="overflow-visible"
        >
          {{#each this.segments as |seg i|}}
            <path
              data-test-chart-segment
              d={{seg.path}}
              fill={{seg.color}}
              class="transition-transform duration-150 origin-center cursor-pointer hover:brightness-110
                {{if (isEqual this.hoveredIndex i) 'tio-chart-segment--active'}}"
              role="button"
              tabindex="0"
              aria-label="{{seg.data.category}}: {{seg.valueLabel}} ({{seg.percent}}%)"
              {{on "mouseenter" (fn this.setHovered i)}}
              {{on "mousemove" this.trackPointer}}
              {{on "mouseleave" this.clearHovered}}
              {{on "focus" (fn this.setHovered i)}}
              {{on "focus" this.trackFocus}}
              {{on "blur" this.clearHovered}}
              {{on "click" (fn this.handleSegmentClick seg)}}
              {{on "keydown" (fn this.handleSegmentKeydown seg)}}
            />
          {{/each}}

          {{! Center — the total; per-slice detail is in the floating tooltip. }}
          <text
            x={{this.cx}}
            y={{this.cy}}
            text-anchor="middle"
            dominant-baseline="central"
            class="tio-donut-center-value"
          >
            {{this.formattedTotal}}
          </text>
          <text
            x={{this.cx}}
            y={{add this.cy 20}}
            text-anchor="middle"
            dominant-baseline="central"
            class="tio-donut-center-label"
          >
            Total
          </text>
        </svg>
      </div>

      {{! Floating tooltip anchored to the cursor — consistent with the bar/line/area charts. }}
      {{#if this.tooltipData}}
        <div
          data-test-chart-tooltip
          class="tio-chart-tooltip -translate-x-1/2 -translate-y-full"
          {{style left=(concat this.tooltipX "px") top=(concat this.tooltipY "px")}}
          {{clampTooltip (concat this.tooltipX ":" this.tooltipY)}}
        >
          <div class="font-medium">{{this.tooltipData.category}}</div>
          <div class="tio-text-muted">{{this.tooltipData.value}}
            ({{this.tooltipData.percent}}%)</div>
        </div>
      {{/if}}

      {{! Legend }}
      {{#if this.showLegend}}
        <div data-test-chart-legend class={{this.legendClass}}>
          {{#each this.legendItems as |item|}}
            <div class="tio-row-snug">
              <span
                class="tio-chart-dot-swatch tio-chart-dot-swatch--lg"
                {{style color=item.color}}
              ></span>
              <span class="tio-text-default font-medium">{{item.label}}</span>
              <span class="tio-text-muted">({{item.percent}}%)</span>
            </div>
          {{/each}}
        </div>
      {{/if}}
    </div>
  </template>
}

function isEqual(a: unknown, b: unknown) {
  return a === b;
}

function add(a: number, b: number) {
  return a + b;
}

export type { DonutChartSignature };
